import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthenticationWrapper';
import WalletService from '../../rest/walletService';
import PaymentService from '../../rest/paymentsService';
import TopNavBar from '../../components/reusables/topNavBar';
import AsaasSeal from '../../components/reusables/asaasSeal';
import { toast } from 'react-toastify';
import { ConfirmCancelAnticipationModal } from './components/confirmCancelAnticipationModal';
import {
    MdAccountBalanceWallet, MdTrendingUp, MdAccessTime, MdClose,
    MdOutlineArrowUpward, MdOutlineArrowDownward, MdChevronRight, MdCancel,
    MdMoneyOff, MdRefresh
} from 'react-icons/md';
import { AiOutlineLoading } from 'react-icons/ai';
import { formatCurrency } from '../../utils/CurrencyUtils';
import { getBrazilDateString } from '../../utils/DateUtils';


const TABS = ['Resumo', 'Extrato', 'Reembolsos', 'Taxas'];

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const clean = dateStr.replace('T', ' ');
    const [y, m, d] = clean.slice(0, 10).split('-');
    const time = clean.length > 10 ? clean.slice(11, 16) : null;
    return time ? `${d}/${m}/${y} ${time}` : `${d}/${m}/${y}`;
};

const BILLING_TYPE_LABELS = {
    CREDIT_CARD: 'Cartão de Crédito',
    PIX: 'PIX',
    BOLETO: 'Boleto',
    DEBIT_CARD: 'Cartão de Débito',
};

const FINANCIAL_TX_TYPE_LABELS = {
    // Recebimentos
    PAYMENT:                            'Pagamento recebido',
    PAYMENT_RECEIVED:                   'Pagamento recebido',
    SUBSCRIPTION_PAYMENT:               'Pagamento de assinatura',
    SUBSCRIPTION_PAYMENT_RECEIVED:      'Pagamento de assinatura',
    INSTALLMENT_PAYMENT_RECEIVED:       'Pagamento de parcela',
    CREDIT:                             'Crédito',
    CREDIT_RECEIVED:                    'Crédito recebido',
    // Débitos
    DEBIT:                              'Débito',
    DEBIT_GENERATED:                    'Débito',
    // Reembolsos
    REFUND:                             'Reembolso',
    PAYMENT_REFUND:                     'Reembolso',
    REFUND_REVERSAL:                    'Estorno de reembolso',
    PAYMENT_REFUND_CANCELLED:           'Reembolso cancelado',
    CREDIT_CARD_REFUND:                 'Estorno de cartão de crédito',
    PAYMENT_REVERSAL:                   'Reversão de pagamento',
    PAYMENT_REVERSED:                   'Pagamento revertido',
    // Chargebacks
    CHARGEBACK:                         'Chargeback',
    PAYMENT_CHARGEBACK_REQUEST:         'Solicitação de chargeback',
    PAYMENT_CHARGEBACK_DISPUTE:         'Disputa de chargeback',
    CHARGEBACK_DISPUTE:                 'Disputa de chargeback',
    // Antecipações
    PAYMENT_ANTICIPATION:               'Antecipação de recebível',
    PAYMENT_ANTICIPATION_CREATED:       'Antecipação de recebível',
    ANTICIPATED_PAYMENT:                'Pagamento antecipado',
    ANTICIPATED_PAYMENT_RECEIVED:       'Recebimento antecipado',
    RECEIVED_ANTICIPATION_FEE:          'Taxa de antecipação',
    PAYMENT_ANTICIPATION_FEE:           'Taxa de antecipação',
    // Transferências
    TRANSFER:                           'Transferência',
    TRANSFER_SENT:                      'Transferência enviada',
    TRANSFER_RECEIVED:                  'Transferência recebida',
    TRANSFER_FEE:                       'Taxa de transferência',
    BALANCE_TRANSFER:                   'Transferência de saldo',
    BALANCE_TRANSFER_IN:                'Entrada de transferência',
    BALANCE_TRANSFER_OUT:               'Saída de transferência',
    // Taxas e mensalidades
    PAYMENT_FEE:                        'Taxa de cobrança',
    PAYMENT_PAYER_FEE:                  'Taxa do pagador',
    SUBSCRIPTION_PAYMENT_FEE:           'Taxa de assinatura',
    TRANSFER_FEE_DEBIT:                 'Taxa de transferência',
    NOTIFICATION_MESSAGE_SENT_FEE:      'Taxa de notificação',
    MONTHLY_FEE:                        'Mensalidade Asaas',
    MONTHLY_FEE_CHARGED:                'Mensalidade Asaas',
    INVOICE_PAYMENT:                    'Mensalidade Asaas',
    ASAAS_ACCOUNT_FEE:                  'Mensalidade Asaas',
    ASAAS_FEE:                          'Taxa Asaas',
    // Outros
    DEBT_RECOVERY_PAYMENT:              'Recuperação de inadimplência',
    SPLIT:                              'Split de pagamento',
    PAYER_CASH_BACK:                    'Cashback',
};

const ANTICIPATION_STATUS_LABELS = {
    PENDING:           { label: 'Pendente',              cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    AWAITING_APPROVAL: { label: 'Aguardando aprovação',  cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    CREDITED:          { label: 'Creditado',             cls: 'bg-green-50 text-green-700 border-green-200' },
    APPROVED:          { label: 'Aprovado',              cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    MANUAL_ANALYSIS:   { label: 'Em análise manual',     cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    DENIED:            { label: 'Negado',                cls: 'bg-red-50 text-red-700 border-red-200' },
    CANCELLED:         { label: 'Cancelado',             cls: 'bg-gray-50 text-gray-500 border-gray-200' },
    SCHEDULED:         { label: 'Agendado',              cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    DEBITED:           { label: 'Debitado',              cls: 'bg-purple-50 text-purple-700 border-purple-200' },
    OVERDUE:           { label: 'Vencido',               cls: 'bg-red-50 text-red-700 border-red-200' },
};

const TRANSFER_STATUS_LABELS = {
    DONE:      { label: 'Concluída',  cls: 'bg-green-50 text-green-700 border-green-200' },
    PENDING:   { label: 'Pendente',   cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    FAILED:    { label: 'Falhou',     cls: 'bg-red-50 text-red-700 border-red-200' },
    CANCELLED: { label: 'Cancelada',  cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};


function StatusBadge({ status, map }) {
    const s = map[status] || { label: status, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
}

function LoadingSpinner() {
    return (
        <div className="flex justify-center items-center py-12">
            <AiOutlineLoading className="animate-spin text-custom_yellow text-3xl" />
        </div>
    );
}

// ── Tab 0: Resumo ─────────────────────────────────────────────────────────────

function InlinePaymentList({ title, icon, payments, hasMore, onVerMais, onRowClick, isConfirmed }) {
    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                {icon}
                <h3 className="font-medium text-gray-700 text-sm">{title}</h3>
            </div>
            {payments.length === 0 ? (
                <p className="text-center text-gray-400 py-6 text-sm">Nenhuma cobrança encontrada.</p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-gray-400 text-xs uppercase tracking-wide border-b border-gray-100">
                                    <th className="text-left px-4 py-2">{isConfirmed ? 'Data de Confirmação' : 'Vencimento'}</th>
                                    <th className="text-left px-4 py-2">Tipo</th>
                                    <th className="text-right px-4 py-2">Valor</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map(p => (
                                    <tr
                                        key={p.id}
                                        onClick={() => p.id && onRowClick(p)}
                                        className={`border-t border-gray-50 hover:bg-gray-50 ${p.id ? 'cursor-pointer' : ''}`}>
                                        <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{formatDate(p.dueDate)}</td>
                                        <td className="px-4 py-2 text-gray-600">{BILLING_TYPE_LABELS[p.billingType] || p.billingType}</td>
                                        <td className="px-4 py-2 text-right font-medium text-gray-800">{formatCurrency(p.value)}</td>
                                        <td className="px-4 py-2 text-gray-400">{p.id ? <MdChevronRight size={18} /> : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {hasMore && (
                        <div className="text-center py-3 border-t border-gray-100">
                            <button
                                onClick={onVerMais}
                                className="text-sm text-custom_yellow hover:underline font-medium">
                                Ver Mais
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function ResumoTab({ clientCode }) {
    const [balance, setBalance]                     = useState(null);
    const [pending, setPending]                     = useState(null);
    const [confirmed, setConfirmed]                 = useState(null);
    const [overdue, setOverdue]                     = useState(null);
    const [pendingPayments, setPendingPayments]     = useState([]);
    const [confirmedPayments, setConfirmedPayments] = useState([]);
    const [overduePayments, setOverduePayments]     = useState([]);
    const [pendingHasMore, setPendingHasMore]       = useState(false);
    const [confirmedHasMore, setConfirmedHasMore]   = useState(false);
    const [overdueHasMore, setOverdueHasMore]       = useState(false);
    const [loading, setLoading]                     = useState(true);

    const [detailModal, setDetailModal]     = useState(null); // { title, status }
    const [detailData, setDetailData]       = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailOffset, setDetailOffset]   = useState(0);
    const [detailHasMore, setDetailHasMore] = useState(false);
    const PAGE = 10;

    const [chargeDetail, setChargeDetail]         = useState(null); // payment row that was clicked
    const [chargeDetailData, setChargeDetailData] = useState(null);
    const [chargeDetailLoading, setChargeDetailLoading] = useState(false);

    const openChargeDetail = async (payment) => {
        setChargeDetail(payment);
        setChargeDetailData(null);
        setChargeDetailLoading(true);
        try {
            const r = await WalletService.getChargeDetails(clientCode, payment.id);
            setChargeDetailData(r);
        } catch {
            toast.error('Erro ao carregar detalhes da cobrança.');
        } finally {
            setChargeDetailLoading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                const [bal, pend, conf, ovd, pendPay, confPay, ovdPay] = await Promise.all([
                    WalletService.getBalance(clientCode),
                    WalletService.getSummary(clientCode, 'PENDING'),
                    WalletService.getSummary(clientCode, 'CONFIRMED'),
                    WalletService.getSummary(clientCode, 'OVERDUE'),
                    WalletService.listPayments(clientCode, 'PENDING', 0, 10),
                    WalletService.listPayments(clientCode, 'CONFIRMED', 0, 10),
                    WalletService.listPayments(clientCode, 'OVERDUE', 0, 10),
                ]);
                setBalance(bal);
                setPending(pend);
                setConfirmed(conf);
                setOverdue(ovd);
                setPendingPayments(pendPay.data || []);
                setPendingHasMore(pendPay.hasMore || false);
                setConfirmedPayments(confPay.data || []);
                setConfirmedHasMore(confPay.hasMore || false);
                setOverduePayments(ovdPay.data || []);
                setOverdueHasMore(ovdPay.hasMore || false);
            } catch {
                toast.error('Erro ao carregar resumo da carteira.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const openDetail = async (title, status) => {
        setDetailModal({ title, status });
        setDetailOffset(0);
        setDetailData([]);
        setDetailLoading(true);
        try {
            const r = await WalletService.listPayments(clientCode, status, 0, PAGE);
            setDetailData(r.data || []);
            setDetailHasMore(r.hasMore || false);
            setDetailOffset(PAGE);
        } catch {
            toast.error('Erro ao carregar detalhes.');
        } finally {
            setDetailLoading(false);
        }
    };

    const loadMoreDetail = async () => {
        setDetailLoading(true);
        try {
            const r = await WalletService.listPayments(clientCode, detailModal.status, detailOffset, PAGE);
            setDetailData(p => [...p, ...(r.data || [])]);
            setDetailHasMore(r.hasMore || false);
            setDetailOffset(p => p + PAGE);
        } catch {
            toast.error('Erro ao carregar mais.');
        } finally {
            setDetailLoading(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <SummaryCard
                    icon={<MdAccountBalanceWallet className="text-green-600 text-2xl" />}
                    bg="bg-green-50 border-green-200"
                    label="Saldo Disponível"
                    value={formatCurrency(balance?.balance ?? 0)}
                />
                <SummaryCard
                    icon={<MdAccessTime className="text-yellow-600 text-2xl" />}
                    bg="bg-yellow-50 border-yellow-200"
                    label="A Receber (Pendente)"
                    value={formatCurrency(pending?.grossValue ?? 0)}
                    sub={`${pending?.quantity ?? 0} cobranças`}
                />
                <SummaryCard
                    icon={<MdTrendingUp className="text-blue-600 text-2xl" />}
                    bg="bg-blue-50 border-blue-200"
                    label="Em Processamento (Confirmado)"
                    value={formatCurrency(confirmed?.grossValue ?? 0)}
                    sub={`${confirmed?.quantity ?? 0} cobranças`}
                />
                <SummaryCard
                    icon={<MdCancel className="text-red-600 text-2xl" />}
                    bg="bg-red-50 border-red-200"
                    label="Cancelados / Expirados"
                    value={formatCurrency(overdue?.grossValue ?? 0)}
                    sub={`${overdue?.quantity ?? 0} cobranças`}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <InlinePaymentList
                    title="A Receber — Pendente"
                    icon={<MdAccessTime className="text-yellow-500 text-base" />}
                    payments={pendingPayments}
                    hasMore={pendingHasMore}
                    onVerMais={() => openDetail('A Receber — Pendente', 'PENDING')}
                    onRowClick={openChargeDetail}
                />
                <InlinePaymentList
                    title="Em Processamento — Confirmado"
                    icon={<MdTrendingUp className="text-blue-500 text-base" />}
                    payments={confirmedPayments}
                    hasMore={confirmedHasMore}
                    onVerMais={() => openDetail('Em Processamento — Confirmado', 'CONFIRMED')}
                    onRowClick={openChargeDetail}
                    isConfirmed
                />
                <InlinePaymentList
                    title="Cancelados / Expirados"
                    icon={<MdCancel className="text-red-500 text-base" />}
                    payments={overduePayments}
                    hasMore={overdueHasMore}
                    onVerMais={() => openDetail('Cancelados / Expirados', 'OVERDUE')}
                    onRowClick={openChargeDetail}
                />
            </div>

            {detailModal && (
                <DetailModal
                    title={detailModal.title}
                    data={detailData}
                    loading={detailLoading}
                    hasMore={detailHasMore}
                    onLoadMore={loadMoreDetail}
                    onClose={() => setDetailModal(null)}
                    onRowClick={openChargeDetail}
                    isConfirmed={detailModal.status === 'CONFIRMED'}
                />
            )}

            {chargeDetail && (
                <ChargeDetailModal
                    payment={chargeDetail}
                    details={chargeDetailData}
                    loading={chargeDetailLoading}
                    onClose={() => { setChargeDetail(null); setChargeDetailData(null); }}
                />
            )}

            <div className="border-t border-gray-200 pt-6">
                <AntecipacoesTransferenciasTab clientCode={clientCode} />
            </div>
        </div>
    );
}

function SummaryCard({ icon, bg, label, value, sub }) {
    return (
        <div className={`border rounded-xl p-5 flex flex-col gap-2 ${bg}`}>
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-sm font-medium text-gray-600">{label}</span>
            </div>
            <span className="text-2xl font-bold text-gray-800">{value}</span>
            {sub && <span className="text-xs text-gray-500">{sub}</span>}
        </div>
    );
}

function DetailModal({ title, data, loading, hasMore, onLoadMore, onClose, onRowClick, isConfirmed }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="font-semibold text-lg text-gray-800">{title}</h2>
                    <button onClick={onClose}><MdClose className="text-gray-500 text-xl hover:text-gray-800" /></button>
                </div>
                <div className="overflow-y-auto flex-1 px-6 py-4">
                    {data.length === 0 && !loading && (
                        <p className="text-center text-gray-400 py-8">Nenhuma cobrança encontrada.</p>
                    )}
                    {data.length > 0 && (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-gray-500 border-b">
                                    <th className="text-left py-2">Tipo</th>
                                    <th className="text-left py-2">{isConfirmed ? 'Data de Confirmação' : 'Vencimento'}</th>
                                    <th className="text-right py-2">Valor</th>
                                    <th className="py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map(p => (
                                    <tr
                                        key={p.id}
                                        onClick={() => p.id && onRowClick(p)}
                                        className={`border-b last:border-0 hover:bg-gray-50 ${p.id ? 'cursor-pointer' : ''}`}>
                                        <td className="py-2">{BILLING_TYPE_LABELS[p.billingType] || p.billingType}</td>
                                        <td className="py-2">{formatDate(p.dueDate)}</td>
                                        <td className="py-2 text-right font-medium">{formatCurrency(p.value)}</td>
                                        <td className="py-2 pl-2 text-gray-400">{p.id ? <MdChevronRight size={18} /> : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {loading && <LoadingSpinner />}
                    {hasMore && !loading && (
                        <div className="text-center mt-4">
                            <button onClick={onLoadMore}
                                className="text-sm text-custom_yellow hover:underline font-medium">
                                Carregar mais
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Charge Detail Modal ───────────────────────────────────────────────────────

const ENTITY_TYPE_LABELS = {
    ORDER:           'Pedido',
    RESERVATION:     'Reserva',
    CREDIT_RECHARGE: 'Recarga de Crédito',
};

const ENTITY_STATUS_LABELS = {
    // order statuses
    PROCESSINGPAYMENT: 'Aguardando pagamento',
    COMPLETED:         'Concluído',
    READYTODELIVER:    'Pronto para retirada',
    FINISHED:          'Finalizado',
    CANCELED:          'Cancelado',
    // reservation statuses
    PENDING:           'Pendente',
    RELEASED:          'Liberada',
    // asaas statuses (credit recharge)
    CONFIRMED:         'Confirmado',
    FAILED:            'Falhou',
};

function DetailRow({ label, value }) {
    if (value == null) return null;
    return (
        <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-sm font-medium text-gray-800 text-right max-w-[60%]">{value}</span>
        </div>
    );
}

function ChargeDetailModal({ payment, details, loading, onClose }) {
    const dateLabel = details?.entityType === 'RESERVATION' ? 'Data da refeição'
                    : details?.entityType === 'CREDIT_RECHARGE' ? 'Data da transação'
                    : 'Data do pedido';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="font-semibold text-lg text-gray-800">Detalhes da Cobrança</h2>
                    <button onClick={onClose}><MdClose className="text-gray-500 text-xl hover:text-gray-800" /></button>
                </div>
                <div className="px-6 py-5">
                    <div className="mb-4 pb-4 border-b border-gray-100">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Cobrança</p>
                        <DetailRow label="Forma de pagamento" value={BILLING_TYPE_LABELS[payment.billingType] || payment.billingType} />
                        <DetailRow label="Bandeira" value={payment.creditCardBrand} />
                        <DetailRow label="Final do Cartão" value={payment.creditCardNumber} />
                        <DetailRow label="Valor" value={formatCurrency(payment.value)} />
                        <DetailRow label="Valor Líquido" value={payment.netValue != null ? formatCurrency(payment.netValue) : null} />
                        <DetailRow label={payment.status === 'CONFIRMED' ? 'Data de Confirmação' : 'Vencimento'} value={formatDate(payment.dueDate)} />
                        <DetailRow label="Data de Crédito Estimada" value={formatDate(payment.estimatedCreditDate)} />
                    </div>

                    {loading && <LoadingSpinner />}

                    {!loading && !details && (
                        <p className="text-center text-gray-400 py-4 text-sm">Não foi possível carregar os detalhes.</p>
                    )}

                    {!loading && details && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <p className="text-xs text-gray-400 uppercase tracking-wide">
                                    {ENTITY_TYPE_LABELS[details.entityType] || details.entityType}
                                </p>
                                {details.entityStatus && (
                                    <span className="text-xs px-2 py-0.5 rounded-full border bg-gray-50 text-gray-600 border-gray-200">
                                        {ENTITY_STATUS_LABELS[details.entityStatus] || details.entityStatus}
                                    </span>
                                )}
                            </div>
                            {details.entityNumericId != null && details.entityType === 'ORDER' && (
                                <DetailRow label="Nº do Pedido" value={`#${details.entityNumericId}`} />
                            )}
                            {details.entityNumericId != null && details.entityType === 'CREDIT_RECHARGE' && (
                                <DetailRow label="Nº da Transação" value={`#${details.entityNumericId}`} />
                            )}
                            <DetailRow label="Cliente" value={details.customerName} />
                            <DetailRow label={dateLabel} value={formatDateTime(details.primaryDate)} />
                            {details.turnId != null && (
                                <DetailRow label="Turno" value={`Turno ${details.turnId}`} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Tab 1: Extrato ────────────────────────────────────────────────────────────

function ExtratoTab({ clientCode }) {
    const today = getBrazilDateString();
    const [brazilYear, brazilMonth] = today.split('-');
    const firstDay = `${brazilYear}-${brazilMonth}-01`;

    const [startDate, setStartDate]   = useState(firstDay);
    const [finishDate, setFinishDate] = useState(today);
    const [data, setData]             = useState([]);
    const [hasMore, setHasMore]       = useState(false);
    const [offset, setOffset]         = useState(0);
    const [loading, setLoading]       = useState(false);
    const [searched, setSearched]     = useState(false);
    const PAGE = 20;

    const search = useCallback(async (reset = true) => {
        setLoading(true);
        if (reset) { setData([]); setOffset(0); }
        try {
            const r = await WalletService.listTransactions(clientCode, {
                startDate, finishDate, order: 'DESC',
                offset: reset ? 0 : offset, limit: PAGE
            });
            setData(prev => reset ? (r.data || []) : [...prev, ...(r.data || [])]);
            setHasMore(r.hasMore || false);
            setOffset(reset ? PAGE : offset + PAGE);
            setSearched(true);
        } catch {
            toast.error('Erro ao carregar extrato.');
        } finally {
            setLoading(false);
        }
    }, [startDate, finishDate, offset]);

    const txColor = (value) => value >= 0 ? 'text-green-600' : 'text-red-600';
    const txIcon  = (value) => value >= 0
        ? <MdOutlineArrowUpward className="text-green-500" />
        : <MdOutlineArrowDownward className="text-red-500" />;

    return (
        <div>
            <div className="flex flex-wrap gap-3 items-end mb-6">
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 font-medium">Data inicial</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom_yellow" />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 font-medium">Data final</label>
                    <input type="date" value={finishDate} onChange={e => setFinishDate(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom_yellow" />
                </div>
                <button
                    onClick={() => search(true)}
                    disabled={loading}
                    className="bg-custom_yellow text-white px-5 py-2 rounded-lg text-sm font-medium hover:brightness-95 transition disabled:opacity-60">
                    {loading ? 'Buscando...' : 'Buscar'}
                </button>
            </div>

            {!searched && !loading && (
                <p className="text-center text-gray-400 py-12">Selecione um período e clique em Buscar.</p>
            )}

            {searched && data.length === 0 && !loading && (
                <p className="text-center text-gray-400 py-12">Nenhuma movimentação no período.</p>
            )}

            {data.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr className="text-gray-500 text-xs uppercase tracking-wide">
                                <th className="text-left px-4 py-3">Data</th>
                                <th className="text-left px-4 py-3">Descrição</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-right px-4 py-3">Valor</th>
                                <th className="text-right px-4 py-3">Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((tx, i) => (
                                <tr key={tx.id || i} className="border-t hover:bg-gray-50">
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(tx.date)}</td>
                                    <td className="px-4 py-3 text-gray-700">{tx.description || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500">{FINANCIAL_TX_TYPE_LABELS[tx.type] || tx.type}</td>
                                    <td className={`px-4 py-3 text-right font-medium ${txColor(tx.value)}`}>
                                        <span className="inline-flex items-center gap-1 justify-end">
                                            {txIcon(tx.value)}{formatCurrency(Math.abs(tx.value))}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-600">
                                        {tx.balance != null ? formatCurrency(tx.balance) : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {loading && <LoadingSpinner />}

            {hasMore && !loading && (
                <div className="text-center mt-4">
                    <button onClick={() => search(false)}
                        className="text-sm text-custom_yellow hover:underline font-medium">
                        Carregar mais
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Tab 2: Antecipações + Transferências ──────────────────────────────────────

function AntecipacoesTransferenciasTab({ clientCode }) {
    const { hasPermission } = useAuth();
    const canAnticipate = hasPermission('wallet', 'anticipate');
    const canTransfer   = hasPermission('wallet', 'transfer');
    const [anticipations, setAnticipations]   = useState([]);
    const [limits, setLimits]                 = useState(null);
    const [transfers, setTransfers]           = useState([]);
    const [loading, setLoading]               = useState(true);
    const [antHasMore, setAntHasMore]         = useState(false);
    const [antOffset, setAntOffset]           = useState(0);
    const [trHasMore, setTrHasMore]           = useState(false);
    const [trOffset, setTrOffset]             = useState(0);

    const [simulateModal, setSimulateModal]         = useState(false);
    const [transferModal, setTransferModal]         = useState(false);
    const [selectedAnticipation, setSelectedAnticipation] = useState(null);
    const PAGE = 10;

    const loadData = useCallback(async () => {
        try {
            const [ants, lim, trs] = await Promise.all([
                WalletService.listAnticipations(clientCode, null, 0, PAGE),
                WalletService.getAnticipationLimits(clientCode),
                WalletService.listTransfers(clientCode, { offset: 0, limit: PAGE }),
            ]);
            setAnticipations(ants.data || []);
            setAntHasMore(ants.hasMore || false);
            setAntOffset(PAGE);
            setLimits(lim);
            setTransfers(trs.data || []);
            setTrHasMore(trs.hasMore || false);
            setTrOffset(PAGE);
        } catch {
            toast.error('Erro ao carregar antecipações e transferências.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const loadMoreAnts = async () => {
        try {
            const r = await WalletService.listAnticipations(clientCode, null, antOffset, PAGE);
            setAnticipations(p => [...p, ...(r.data || [])]);
            setAntHasMore(r.hasMore || false);
            setAntOffset(p => p + PAGE);
        } catch { toast.error('Erro ao carregar mais.'); }
    };

    const loadMoreTrs = async () => {
        try {
            const r = await WalletService.listTransfers(clientCode, { offset: trOffset, limit: PAGE });
            setTransfers(p => [...p, ...(r.data || [])]);
            setTrHasMore(r.hasMore || false);
            setTrOffset(p => p + PAGE);
        } catch { toast.error('Erro ao carregar mais.'); }
    };

    const onTransferCreated = (transfer) => {
        setTransfers(p => [transfer, ...p]);
        setTransferModal(false);
        toast.success('Transferência criada com sucesso!');
    };

    const onAnticipationCreated = () => {
        setSimulateModal(false);
        loadData();
        toast.success('Antecipação solicitada com sucesso!');
    };

    const onAnticipationCancelled = () => {
        setSelectedAnticipation(null);
        loadData();
        toast.success('Antecipação cancelada com sucesso!');
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-8">
            {/* ── Antecipações ── */}
            <section>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-gray-700 text-base">Antecipações</h2>
                    {canAnticipate && (
                        <button
                            onClick={() => setSimulateModal(true)}
                            className="bg-custom_yellow text-white px-4 py-2 rounded-lg text-sm font-medium hover:brightness-95 transition">
                            Simular Antecipação
                        </button>
                    )}
                </div>

                {limits && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <LimitsCard label="Disponível para antecipar" value={formatCurrency(limits.anticipableValue ?? 0)} />
                        <LimitsCard label="Total antecipável" value={formatCurrency(limits.totalAnticipableValue ?? 0)} />
                        <LimitsCard label="Limite máximo" value={formatCurrency(limits.maxTotalLimit ?? 0)} />
                    </div>
                )}

                {anticipations.length === 0 && (
                    <p className="text-gray-400 text-sm py-4">Nenhuma antecipação encontrada.</p>
                )}

                {anticipations.length > 0 && (
                    <div className="border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr className="text-gray-500 text-xs uppercase tracking-wide">
                                    <th className="text-left px-4 py-3">Data</th>
                                    <th className="text-left px-4 py-3">ID Cobrança</th>
                                    <th className="text-right px-4 py-3">Valor Solicitado</th>
                                    <th className="text-right px-4 py-3">Valor Líquido</th>
                                    <th className="text-right px-4 py-3">Taxa</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {anticipations.map(a => (
                                    <tr key={a.id}
                                        onClick={() => setSelectedAnticipation(a)}
                                        className="border-t hover:bg-gray-50 cursor-pointer">
                                        <td className="px-4 py-3 text-gray-500">{formatDate(a.anticipationDate || a.dateCreated)}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-400">{a.paymentId || '—'}</td>
                                        <td className="px-4 py-3 text-right">{formatCurrency(a.requestedValue)}</td>
                                        <td className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(a.netValue)}</td>
                                        <td className="px-4 py-3 text-right text-red-500">{formatCurrency(a.anticipationFee)}</td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={a.status} map={ANTICIPATION_STATUS_LABELS} />
                                        </td>
                                        <td className="px-4 py-3 text-gray-400">
                                            <MdChevronRight size={18} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {antHasMore && (
                    <div className="text-center mt-3">
                        <button onClick={loadMoreAnts} className="text-sm text-custom_yellow hover:underline font-medium">
                            Carregar mais
                        </button>
                    </div>
                )}
            </section>

            {/* ── Transferências ── */}
            <section>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-gray-700 text-base">Transferências</h2>
                    {canTransfer && (
                        <button
                            onClick={() => setTransferModal(true)}
                            className="bg-custom_yellow text-white px-4 py-2 rounded-lg text-sm font-medium hover:brightness-95 transition">
                            Nova Transferência
                        </button>
                    )}
                </div>

                {transfers.length === 0 && (
                    <p className="text-gray-400 text-sm py-4">Nenhuma transferência encontrada.</p>
                )}

                {transfers.length > 0 && (
                    <div className="border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr className="text-gray-500 text-xs uppercase tracking-wide">
                                    <th className="text-left px-4 py-3">Data</th>
                                    <th className="text-left px-4 py-3">Tipo</th>
                                    <th className="text-left px-4 py-3">Destino</th>
                                    <th className="text-left px-4 py-3">Descrição</th>
                                    <th className="text-right px-4 py-3">Valor</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transfers.map(t => (
                                    <tr key={t.id} className="border-t hover:bg-gray-50">
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                            {formatDate(t.transferDate || t.dateCreated)}
                                        </td>
                                        <td className="px-4 py-3 font-medium">{t.operationType || t.type || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600 text-xs">
                                            {t.pixAddressKey
                                                ? `${t.pixAddressKeyType}: ${t.pixAddressKey}`
                                                : t.bankAccount
                                                    ? `${t.bankAccount.bankName || t.bankAccount.bankCode} ag.${t.bankAccount.agency} cc.${t.bankAccount.account}`
                                                    : '—'
                                            }
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{t.description || '—'}</td>
                                        <td className="px-4 py-3 text-right font-medium text-red-600">
                                            − {formatCurrency(t.value)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={t.status} map={TRANSFER_STATUS_LABELS} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {trHasMore && (
                    <div className="text-center mt-3">
                        <button onClick={loadMoreTrs} className="text-sm text-custom_yellow hover:underline font-medium">
                            Carregar mais
                        </button>
                    </div>
                )}
            </section>

            {simulateModal && (
                <SimulateAnticipationModal
                    clientCode={clientCode}
                    onClose={() => setSimulateModal(false)}
                    onConfirmed={onAnticipationCreated}
                />
            )}

            {transferModal && (
                <TransferModal
                    clientCode={clientCode}
                    onClose={() => setTransferModal(false)}
                    onCreated={onTransferCreated}
                />
            )}

            {selectedAnticipation && (
                <AnticipationDetailModal
                    anticipation={selectedAnticipation}
                    clientCode={clientCode}
                    onClose={() => setSelectedAnticipation(null)}
                    onCancelled={onAnticipationCancelled}
                />
            )}
        </div>
    );
}

function AnticipationDetailModal({ anticipation: a, clientCode, onClose, onCancelled }) {
    const [cancelling, setCancelling]           = useState(false);
    const [showTerms, setShowTerms]             = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const isCancellable = a.status === 'PENDING' || a.status === 'AWAITING_APPROVAL';

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await WalletService.cancelAnticipation(clientCode, a.id);
            onCancelled();
        } catch {
            toast.error('Erro ao cancelar antecipação.');
        } finally {
            setCancelling(false);
        }
    };

    return (
        <>
            <Modal title="Detalhes da Antecipação" onClose={onClose}>
                <div className="space-y-1">
                    <div className="mb-3">
                        <StatusBadge status={a.status} map={ANTICIPATION_STATUS_LABELS} />
                    </div>
                    <DetailRow label="ID Asaas" value={a.id} />
                    <DetailRow label="ID da Cobrança" value={a.paymentId} />
                    <DetailRow label="Data de Antecipação" value={formatDate(a.anticipationDate)} />
                    <DetailRow label="Data de Criação" value={formatDate(a.dateCreated)} />
                    <div className="border-t border-gray-100 my-2" />
                    <DetailRow label="Valor Solicitado" value={a.requestedValue != null ? formatCurrency(a.requestedValue) : null} />
                    <DetailRow label="Taxa de Antecipação" value={a.anticipationFee != null ? formatCurrency(a.anticipationFee) : null} />
                    <DetailRow label="Valor Líquido" value={a.netValue != null ? formatCurrency(a.netValue) : null} />
                    {a.terms && (
                        <div className="pt-2">
                            <button
                                onClick={() => setShowTerms(v => !v)}
                                className="text-sm text-blue-600 hover:underline font-medium">
                                {showTerms ? 'Ocultar termos' : 'Ver termos da antecipação'}
                            </button>
                            {showTerms && (
                                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                                    {a.terms}
                                </div>
                            )}
                        </div>
                    )}
                    {isCancellable && (
                        <div className="pt-3">
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                disabled={cancelling}
                                className="w-full bg-red-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition disabled:opacity-60">
                                {cancelling ? 'Cancelando...' : 'Cancelar Antecipação'}
                            </button>
                        </div>
                    )}
                </div>
            </Modal>

            <ConfirmCancelAnticipationModal
                isOpen={showCancelConfirm}
                anticipation={a}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={() => { setShowCancelConfirm(false); handleCancel(); }}
            />
        </>
    );
}

function LimitsCard({ label, value }) {
    return (
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className="text-lg font-bold text-gray-800">{value}</p>
        </div>
    );
}

// ── Simulate Anticipation Modal ───────────────────────────────────────────────

function SimulateAnticipationModal({ clientCode, onClose, onConfirmed }) {
    const [type, setType]             = useState('ORDER');
    const [id, setId]                 = useState('');
    const [simulation, setSimulation] = useState(null);
    const [loading, setLoading]       = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError]           = useState('');

    const handleTypeChange = (newType) => { setType(newType); setId(''); setSimulation(null); setError(''); };

    const simulate = async () => {
        if (!id.trim()) { setError(type === 'ORDER' ? 'Informe o número do pedido.' : 'Informe o ID da transação.'); return; }
        setError('');
        setLoading(true);
        try {
            const r = await WalletService.simulateAnticipation(clientCode, type, id.trim());
            setSimulation(r);
        } catch (e) {
            setError(e.response?.data?.message || (type === 'ORDER'
                ? 'Pedido não encontrado ou sem pagamento via gateway.'
                : 'Transação não encontrada ou sem recarga via gateway.'));
        } finally {
            setLoading(false);
        }
    };

    const confirm = async () => {
        setConfirming(true);
        try {
            await WalletService.createAnticipation(clientCode, type, id.trim());
            onConfirmed();
        } catch {
            toast.error('Erro ao solicitar antecipação.');
        } finally {
            setConfirming(false);
        }
    };

    const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom_yellow";

    return (
        <Modal title="Simular Antecipação" onClose={onClose}>
            <div className="space-y-4">
                <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Tipo</label>
                    <div className="flex gap-2">
                        {[['ORDER', 'Pedido'], ['RECHARGE', 'Recarga de Crédito']].map(([val, label]) => (
                            <button key={val} onClick={() => handleTypeChange(val)}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${type === val
                                    ? 'bg-custom_yellow text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-sm font-medium text-gray-600">
                        {type === 'ORDER' ? 'Nº do Pedido' : 'ID da Transação'}
                    </label>
                    <input
                        type="text"
                        placeholder={type === 'ORDER' ? 'ex: 1234' : 'ex: 5678'}
                        value={id}
                        onChange={e => setId(e.target.value)}
                        className={`mt-1 ${inputCls}`}
                    />
                    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                </div>

                <button
                    onClick={simulate}
                    disabled={loading}
                    className="w-full bg-gray-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition disabled:opacity-60">
                    {loading ? 'Simulando...' : 'Simular'}
                </button>

                {simulation && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Valor original da cobrança</span>
                            <span className="font-medium text-gray-800">{formatCurrency(simulation.totalValue)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">
                                Taxa de cobrança ({simulation.billingType === 'CREDIT_CARD' ? 'Cartão de Crédito' : 'PIX'})
                            </span>
                            <span className="text-red-500">− {formatCurrency(simulation.totalValue - simulation.receivableValue)}</span>
                        </div>
                        <div className="flex justify-between border-t border-yellow-200 pt-2">
                            <span className="text-gray-600">Valor recebível</span>
                            <span className="font-medium text-gray-800">{formatCurrency(simulation.receivableValue)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Taxa de antecipação</span>
                            <span className="text-red-500">− {formatCurrency(simulation.anticipationFee)}</span>
                        </div>
                        <div className="flex justify-between border-t border-yellow-200 pt-2">
                            <span className="text-gray-600 font-medium">Valor recebido após antecipação</span>
                            <span className="font-bold text-green-600">{formatCurrency(simulation.netValue)}</span>
                        </div>
                        {simulation.anticipationDate && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Data prevista</span>
                                <span className="text-gray-700">{formatDate(simulation.anticipationDate)}</span>
                            </div>
                        )}
                        <button
                            onClick={confirm}
                            disabled={confirming}
                            className="w-full mt-2 bg-custom_yellow text-white py-2 rounded-lg text-sm font-medium hover:brightness-95 transition disabled:opacity-60">
                            {confirming ? 'Solicitando...' : 'Confirmar Antecipação'}
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    );
}

// ── Transfer Modal ────────────────────────────────────────────────────────────

const PIX_KEY_TYPES = [
    { value: 'CPF',   label: 'CPF' },
    { value: 'CNPJ',  label: 'CNPJ' },
    { value: 'EMAIL', label: 'E-mail' },
    { value: 'PHONE', label: 'Celular' },
    { value: 'EVP',   label: 'Chave aleatória (EVP)' },
];

const ACCOUNT_TYPES = [
    { value: 'CONTA_CORRENTE', label: 'Conta Corrente' },
    { value: 'CONTA_POUPANCA', label: 'Conta Poupança' },
    { value: 'CONTA_SALARIO',  label: 'Conta Salário' },
];

function TransferModal({ clientCode, onClose, onCreated }) {
    const [tab, setTab]         = useState('PIX');
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState('');

    const [pix, setPix] = useState({
        pixAddressKey: '', pixAddressKeyType: 'CPF', value: '',
        description: '', scheduleDate: ''
    });

    const [ted, setTed] = useState({
        value: '', bankCode: '', ownerName: '', cpfCnpj: '',
        agency: '', account: '', accountDigit: '',
        bankAccountType: 'CONTA_CORRENTE', description: '', scheduleDate: ''
    });

    const update = (setter) => (field) => (e) => setter(p => ({ ...p, [field]: e.target.value }));

    const submit = async () => {
        setError('');
        setLoading(true);
        try {
            const payload = tab === 'PIX'
                ? { type: 'PIX', value: parseFloat(pix.value), pixAddressKey: pix.pixAddressKey,
                    pixAddressKeyType: pix.pixAddressKeyType, description: pix.description || null,
                    scheduleDate: pix.scheduleDate || null }
                : { type: 'TED', value: parseFloat(ted.value), bankCode: ted.bankCode,
                    ownerName: ted.ownerName, cpfCnpj: ted.cpfCnpj, agency: ted.agency,
                    account: ted.account, accountDigit: ted.accountDigit,
                    bankAccountType: ted.bankAccountType, description: ted.description || null,
                    scheduleDate: ted.scheduleDate || null };

            if (!payload.value || isNaN(payload.value) || payload.value <= 0) {
                setError('Informe um valor válido.'); setLoading(false); return;
            }

            const result = await WalletService.createTransfer(clientCode, payload);
            onCreated(result);
        } catch {
            setError('Erro ao criar transferência. Verifique os dados e tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom_yellow";
    const labelCls = "block text-xs font-medium text-gray-600 mb-1";

    return (
        <Modal title="Nova Transferência" onClose={onClose}>
            <div className="flex gap-2 mb-4">
                {['PIX', 'TED'].map(t => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${tab === t
                            ? 'bg-custom_yellow text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {t}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {tab === 'PIX' ? (
                    <>
                        <div>
                            <label className={labelCls}>Valor (R$)</label>
                            <input type="number" min="0.01" step="0.01" placeholder="0,00"
                                value={pix.value} onChange={update(setPix)('value')} className={inputCls} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>Tipo de chave</label>
                                <select value={pix.pixAddressKeyType} onChange={update(setPix)('pixAddressKeyType')} className={inputCls}>
                                    {PIX_KEY_TYPES.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className={labelCls}>Chave PIX</label>
                                <input type="text" value={pix.pixAddressKey} onChange={update(setPix)('pixAddressKey')} className={inputCls} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Descrição (opcional)</label>
                                <input type="text" value={pix.description} onChange={update(setPix)('description')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Agendar para (opcional)</label>
                                <input type="date" value={pix.scheduleDate} onChange={update(setPix)('scheduleDate')} className={inputCls} />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <label className={labelCls}>Valor (R$)</label>
                            <input type="number" min="0.01" step="0.01" placeholder="0,00"
                                value={ted.value} onChange={update(setTed)('value')} className={inputCls} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Código do banco</label>
                                <input type="text" placeholder="ex: 237" value={ted.bankCode} onChange={update(setTed)('bankCode')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Tipo de conta</label>
                                <select value={ted.bankAccountType} onChange={update(setTed)('bankAccountType')} className={inputCls}>
                                    {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>Agência</label>
                                <input type="text" value={ted.agency} onChange={update(setTed)('agency')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Conta</label>
                                <input type="text" value={ted.account} onChange={update(setTed)('account')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Dígito</label>
                                <input type="text" maxLength={1} value={ted.accountDigit} onChange={update(setTed)('accountDigit')} className={inputCls} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Nome do titular</label>
                                <input type="text" value={ted.ownerName} onChange={update(setTed)('ownerName')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>CPF / CNPJ</label>
                                <input type="text" value={ted.cpfCnpj} onChange={update(setTed)('cpfCnpj')} className={inputCls} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Descrição (opcional)</label>
                                <input type="text" value={ted.description} onChange={update(setTed)('description')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Agendar para (opcional)</label>
                                <input type="date" value={ted.scheduleDate} onChange={update(setTed)('scheduleDate')} className={inputCls} />
                            </div>
                        </div>
                    </>
                )}

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <button
                    onClick={submit}
                    disabled={loading}
                    className="w-full bg-custom_yellow text-white py-2.5 rounded-lg text-sm font-medium hover:brightness-95 transition disabled:opacity-60 mt-2">
                    {loading ? 'Enviando...' : `Confirmar transferência ${tab}`}
                </button>
            </div>
        </Modal>
    );
}

// ── Generic Modal wrapper ─────────────────────────────────────────────────────

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="font-semibold text-lg text-gray-800">{title}</h2>
                    <button onClick={onClose}><MdClose className="text-gray-500 text-xl hover:text-gray-800" /></button>
                </div>
                <div className="overflow-y-auto flex-1 px-6 py-5">{children}</div>
            </div>
        </div>
    );
}

// ── Taxas tab ─────────────────────────────────────────────────────────────────

const fmtPct = (v) => v != null ? `${Number(v).toFixed(2)}%` : '—';
const fmtCur = (v) => v != null ? formatCurrency(v) : '—';
const fmtExpiry = (s) => { if (!s) return null; const [y,m,d] = s.slice(0,10).split('-'); return `${d}/${m}/${y}`; };

function FeeSectionHeader({ title, subtitle }) {
    return (
        <div className="col-span-full mt-4 first:mt-0">
            <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            <div className="border-b border-gray-200 mt-2" />
        </div>
    );
}

function FeeCard({ title, children }) {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-800 text-sm mb-3">{title}</h3>
            {children}
        </div>
    );
}

function FeeRow({ label, value, sub, green }) {
    return (
        <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
            <div>
                <span className="text-sm text-gray-600">{label}</span>
                {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
            </div>
            <span className={`text-sm font-semibold ${green ? 'text-green-600' : 'text-gray-800'}`}>{value}</span>
        </div>
    );
}

function DayBadge({ days }) {
    if (!days) return null;
    return <span className="text-xs bg-blue-50 text-blue-600 font-medium px-2 py-0.5 rounded-full">{days}d para receber</span>;
}

function DiscountNote({ date }) {
    if (!date) return null;
    return <p className="text-xs text-green-600 mt-2">Desconto promocional válido até {fmtExpiry(date)}</p>;
}

function CreditsBar({ used, total }) {
    if (total == null) return null;
    const remaining = Math.max(0, total - (used ?? 0));
    const pct = Math.min(100, ((used ?? 0) / total) * 100);
    return (
        <div className="mt-3 p-3 bg-gray-50 rounded-xl">
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{remaining} créditos restantes este mês</span>
                <span>{used ?? 0} / {total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-custom_yellow h-1.5 rounded-full" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function TaxasTab({ clientCode }) {
    const [fees, setFees] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        WalletService.getFees(clientCode)
            .then(setFees)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [clientCode]);

    if (loading) return (
        <div className="flex justify-center py-16">
            <AiOutlineLoading className="animate-spin text-3xl text-custom_yellow" />
        </div>
    );

    if (error || !fees) return (
        <p className="text-center text-gray-400 py-16">Não foi possível carregar as taxas.</p>
    );

    const { payment, transfer, anticipation, notification, creditBureauReport,
            paymentDunning, invoice, pixDebit, childAccount, bill } = fees;
    const cc = payment?.creditCard;
    const bs = payment?.bankSlip;
    const px = payment?.pix;
    const dc = payment?.debitCard;
    const vc = payment?.voucherCard;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-6">

            {/* ── Cobranças recebidas ───────────────────────────────────── */}
            <FeeSectionHeader title="Cobranças recebidas" subtitle="Taxas sobre pagamentos que você recebe" />

            {/* Pix */}
            {px && (
                <FeeCard title="Pix">
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                        <span className="text-sm text-gray-600">Taxa por recebimento</span>
                        <div className="text-right">
                            {px.fixedFeeValueWithDiscount != null && px.fixedFeeValueWithDiscount < px.fixedFeeValue
                                ? <>
                                    <span className="text-sm line-through text-gray-400 mr-2">{fmtCur(px.fixedFeeValue)}</span>
                                    <span className="text-sm font-semibold text-green-600">{fmtCur(px.fixedFeeValueWithDiscount)}</span>
                                  </>
                                : <span className="text-sm font-semibold text-gray-800">{fmtCur(px.fixedFeeValue)}</span>
                            }
                        </div>
                    </div>
                    {px.percentageFee != null && <FeeRow label="Taxa percentual" value={fmtPct(px.percentageFee)} />}
                    <DiscountNote date={px.discountExpiration} />
                    <CreditsBar used={px.creditsReceivedOfCurrentMonth} total={px.monthlyCreditsWithoutFee} />
                </FeeCard>
            )}

            {/* Boleto */}
            {bs && (
                <FeeCard title="Boleto">
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                        <span className="text-sm text-gray-600">Taxa por boleto</span>
                        <div className="text-right">
                            {bs.discountValue != null && bs.discountValue < bs.defaultValue
                                ? <>
                                    <span className="text-sm line-through text-gray-400 mr-2">{fmtCur(bs.defaultValue)}</span>
                                    <span className="text-sm font-semibold text-green-600">{fmtCur(bs.discountValue)}</span>
                                  </>
                                : <span className="text-sm font-semibold text-gray-800">{fmtCur(bs.defaultValue)}</span>
                            }
                        </div>
                    </div>
                    <DiscountNote date={bs.expirationDate} />
                    {bs.daysToReceive != null &&
                        <div className="mt-3 flex justify-end"><DayBadge days={bs.daysToReceive} /></div>}
                </FeeCard>
            )}

            {/* Cartão de Crédito */}
            {cc && (
                <FeeCard title="Cartão de Crédito">
                    {cc.operationValue != null && cc.operationValue > 0 &&
                        <FeeRow label="Taxa operacional" value={fmtCur(cc.operationValue)} sub="por transação" />}
                    <div className="overflow-x-auto mt-2">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left py-1.5 text-gray-400 font-medium">Parcelas</th>
                                    <th className="text-right py-1.5 text-gray-400 font-medium">Taxa</th>
                                    {cc.hasValidDiscount && <th className="text-right py-1.5 text-green-600 font-medium">Promo</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {[
                                    ['1x',       cc.oneInstallmentPercentage,          cc.discountOneInstallmentPercentage],
                                    ['2x – 6x',  cc.upToSixInstallmentsPercentage,     cc.discountUpToSixInstallmentsPercentage],
                                    ['7x – 12x', cc.upToTwelveInstallmentsPercentage,  cc.discountUpToTwelveInstallmentsPercentage],
                                    ['13x – 21x',cc.upToTwentyOneInstallmentsPercentage,cc.discountUpToTwentyOneInstallmentsPercentage],
                                ].map(([label, rate, disc]) => (
                                    <tr key={label}>
                                        <td className="py-2 text-gray-700">{label}</td>
                                        <td className={`py-2 text-right font-medium ${cc.hasValidDiscount ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                            {fmtPct(rate)}
                                        </td>
                                        {cc.hasValidDiscount && (
                                            <td className="py-2 text-right font-semibold text-green-600">{fmtPct(disc)}</td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <DiscountNote date={cc.discountExpiration} />
                    {cc.daysToReceive != null &&
                        <div className="mt-3 flex justify-end"><DayBadge days={cc.daysToReceive} /></div>}
                </FeeCard>
            )}

            {/* Débito + Voucher */}
            {(dc || vc) && (
                <FeeCard title="Débito e Voucher">
                    {dc && <>
                        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Débito</p>
                        {dc.operationValue != null && dc.operationValue > 0 &&
                            <FeeRow label="Taxa operacional" value={fmtCur(dc.operationValue)} />}
                        <FeeRow label="Taxa" value={fmtPct(dc.defaultPercentage)} />
                        {dc.daysToReceive != null &&
                            <div className="flex justify-end mt-1 mb-2"><DayBadge days={dc.daysToReceive} /></div>}
                    </>}
                    {dc && vc && <div className="border-t border-gray-100 my-2" />}
                    {vc && <>
                        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Voucher</p>
                        <FeeRow label="Taxa" value={fmtPct(vc.defaultPercentage)} />
                        {vc.daysToReceive != null &&
                            <div className="flex justify-end mt-1"><DayBadge days={vc.daysToReceive} /></div>}
                    </>}
                </FeeCard>
            )}

            {/* ── Transferências ───────────────────────────────────────── */}
            {transfer && <>
                <FeeSectionHeader title="Transferências enviadas" subtitle="Taxas ao transferir fundos da sua conta" />
                <FeeCard title="Transferências">
                    {transfer.monthlyTransfersWithoutFee != null &&
                        <div className="mb-3 px-3 py-2.5 bg-blue-50 rounded-xl text-sm text-blue-700 font-medium">
                            {transfer.monthlyTransfersWithoutFee} transferências gratuitas por mês
                        </div>}
                    {transfer.pix && (
                        <FeeRow
                            label="Pix"
                            value={transfer.pix.feeValue === 0 ? 'Grátis' : fmtCur(transfer.pix.feeValue)}
                            sub={transfer.pix.consideredInMonthlyTransfersWithoutFee ? 'Conta para o limite mensal' : 'Não conta para o limite mensal'}
                            green={transfer.pix.feeValue === 0}
                        />
                    )}
                    {transfer.ted && (
                        <FeeRow
                            label="TED"
                            value={transfer.ted.feeValue === 0 ? 'Grátis' : fmtCur(transfer.ted.feeValue)}
                            sub={transfer.ted.consideredInMonthlyTransfersWithoutFee ? 'Conta para o limite mensal' : 'Não conta para o limite mensal'}
                            green={transfer.ted.feeValue === 0}
                        />
                    )}
                    {pixDebit && <FeeRow label="Pix Débito" value={fmtCur(pixDebit.feeValue)} />}
                </FeeCard>
            </>}

            {/* ── Antecipação ──────────────────────────────────────────── */}
            {anticipation && <>
                <FeeSectionHeader title="Antecipação de recebíveis" subtitle="Taxa mensal cobrada para antecipar valores" />
                {anticipation.creditCard && (
                    <FeeCard title="Cartão de Crédito">
                        <FeeRow label="À vista (spot)" value={fmtPct(anticipation.creditCard.detachedMonthlyFeeValue)} sub="ao mês" />
                        <FeeRow label="Parcelado" value={fmtPct(anticipation.creditCard.installmentMonthlyFeeValue)} sub="ao mês" />
                    </FeeCard>
                )}
                {(anticipation.bankSlip || anticipation.pix) && (
                    <FeeCard title="Boleto e Pix">
                        {anticipation.bankSlip && <FeeRow label="Boleto" value={fmtPct(anticipation.bankSlip.monthlyFeePercentage)} sub="ao mês" />}
                        {anticipation.pix && <FeeRow label="Pix" value={fmtPct(anticipation.pix.monthlyFeePercentage)} sub="ao mês" />}
                    </FeeCard>
                )}
            </>}

            {/* ── Outros serviços ──────────────────────────────────────── */}
            <FeeSectionHeader title="Outros serviços" />

            {(invoice || paymentDunning || childAccount || bill || creditBureauReport) && (
                <FeeCard title="Serviços adicionais">
                    {invoice && <FeeRow label="Nota Fiscal (NF-e)" value={invoice.feeValue === 0 ? 'Grátis' : fmtCur(invoice.feeValue)} />}
                    {paymentDunning && <FeeRow label="Negativação" value={fmtCur(paymentDunning.feeValue)} />}
                    {childAccount && <FeeRow label="Criação de subconta" value={fmtCur(childAccount.creationFeeValue)} />}
                    {bill && bill.utilityFeeValue != null && <FeeRow label="Pagamento de contas" value={bill.utilityFeeValue === 0 ? 'Grátis' : fmtCur(bill.utilityFeeValue)} green={bill.utilityFeeValue === 0} />}
                    {creditBureauReport && <>
                        <FeeRow label="Consulta Serasa (PF)" value={fmtCur(creditBureauReport.naturalPersonFeeValue)} />
                        <FeeRow label="Consulta Serasa (PJ)" value={fmtCur(creditBureauReport.legalPersonFeeValue)} />
                    </>}
                </FeeCard>
            )}

            {notification && (
                <FeeCard title="Notificações ao cliente">
                    <FeeRow label="SMS" value={notification.smsFeeValue === 0 ? 'Grátis' : fmtCur(notification.smsFeeValue)} green={notification.smsFeeValue === 0} />
                    <FeeRow label="WhatsApp" value={notification.whatsAppFeeValue === 0 ? 'Grátis' : fmtCur(notification.whatsAppFeeValue)} green={notification.whatsAppFeeValue === 0} />
                    <FeeRow label="Ligação" value={notification.phoneCallFeeValue === 0 ? 'Grátis' : fmtCur(notification.phoneCallFeeValue)} green={notification.phoneCallFeeValue === 0} />
                    <FeeRow label="Correios" value={notification.postalServiceFeeValue === 0 ? 'Grátis' : fmtCur(notification.postalServiceFeeValue)} green={notification.postalServiceFeeValue === 0} />
                    {notification.messagingFeeValue != null && notification.messagingFeeValue > 0 &&
                        <FeeRow label="Mensagem" value={fmtCur(notification.messagingFeeValue)} />}
                </FeeCard>
            )}
        </div>
    );
}

// ── Tab 3: Reembolsos ─────────────────────────────────────────────────────────

const REFUND_STATUS_CONFIG = {
    PENDING:          { label: 'Em processamento', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    PROCESSED:        { label: 'Processado',        cls: 'bg-green-50 text-green-700 border-green-200' },
    FAILED:           { label: 'Falhou',            cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    FAILED_PERMANENT: { label: 'Falha permanente',  cls: 'bg-red-50 text-red-700 border-red-200' },
};

const REFUND_ENTITY_LABELS = {
    ORDER:            'Pedido',
    CREDIT_RECHARGE:  'Recarga de Crédito',
};

const STATUS_FILTER_OPTIONS = [
    { value: '',                 label: 'Todos'             },
    { value: 'PENDING',          label: 'Em processamento'  },
    { value: 'PROCESSED',        label: 'Processados'       },
    { value: 'FAILED',           label: 'Com falha'         },
    { value: 'FAILED_PERMANENT', label: 'Falha permanente'  },
];

function RefundStatusBadge({ status }) {
    const cfg = REFUND_STATUS_CONFIG[status] || { label: status, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${cfg.cls}`}>
            {cfg.label}
        </span>
    );
}

function ReembolsosTab({ clientCode }) {
    const [refunds, setRefunds]         = useState([]);
    const [loading, setLoading]         = useState(false);
    const [hasMore, setHasMore]         = useState(false);
    const [offset, setOffset]           = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [searched, setSearched]       = useState(false);
    const [retrying, setRetrying]       = useState(null); // id being retried
    const PAGE = 20;

    const load = useCallback(async (reset = true, currentOffset = 0, currentStatus = statusFilter) => {
        setLoading(true);
        if (reset) { setRefunds([]); }
        try {
            const r = await PaymentService.listRefunds(clientCode, {
                status: currentStatus || undefined,
                offset: reset ? 0 : currentOffset,
                limit: PAGE,
            });
            const items = r.content || [];
            setRefunds(prev => reset ? items : [...prev, ...items]);
            setHasMore(!r.last);
            setOffset(reset ? PAGE : currentOffset + PAGE);
            setSearched(true);
        } catch {
            toast.error('Erro ao carregar histórico de reembolsos.');
        } finally {
            setLoading(false);
        }
    }, [clientCode, statusFilter]);

    // Carrega ao montar
    useEffect(() => { load(true, 0, ''); }, [clientCode]);

    const handleFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
        load(true, 0, newStatus);
    };

    const handleRetry = async (refund) => {
        setRetrying(refund.id);
        try {
            const result = await PaymentService.retryRefund(refund.id, clientCode);
            setRefunds(prev => prev.map(r => r.id === refund.id ? result : r));
            if (result.refundStatus === 'PROCESSED') {
                toast.success('Reembolso processado com sucesso!');
            } else {
                toast.warning('Nova tentativa falhou. Verifique o status.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao retentar reembolso.');
        } finally {
            setRetrying(null);
        }
    };

    return (
        <div>
            {/* Filtros por status */}
            <div className="flex flex-wrap gap-2 mb-6">
                {STATUS_FILTER_OPTIONS.map(opt => (
                    <button
                        key={opt.value}
                        onClick={() => handleFilterChange(opt.value)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            statusFilter === opt.value
                                ? 'bg-custom_yellow text-white border-custom_yellow'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        }`}>
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Estado vazio */}
            {searched && refunds.length === 0 && !loading && (
                <p className="text-center text-gray-400 py-16">Nenhum reembolso encontrado.</p>
            )}

            {/* Tabela */}
            {refunds.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr className="text-gray-500 text-xs uppercase tracking-wide">
                                    <th className="text-left px-4 py-3">Entidade</th>
                                    <th className="text-left px-4 py-3">Nº / ID</th>
                                    <th className="text-left px-4 py-3">Solicitado em</th>
                                    <th className="text-left px-4 py-3">Operador</th>
                                    <th className="text-right px-4 py-3">Valor</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                    <th className="text-center px-4 py-3">Tentativas</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {refunds.map(r => (
                                    <tr key={r.id} className="border-t hover:bg-gray-50">
                                        {/* Tipo de entidade */}
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {REFUND_ENTITY_LABELS[r.entityType] || r.entityType}
                                        </td>

                                        {/* Número / ID */}
                                        <td className="px-4 py-3 font-mono text-xs text-gray-700">
                                            {r.entityNumericId != null
                                                ? `#${r.entityNumericId}`
                                                : r.entityId}
                                        </td>

                                        {/* Data de solicitação */}
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                            {r.requestedAt ? formatDateTime(r.requestedAt) : '—'}
                                        </td>

                                        {/* Operador */}
                                        <td className="px-4 py-3 text-gray-500 text-xs">
                                            {r.requestedBy || '—'}
                                        </td>

                                        {/* Valor */}
                                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                                            {r.amount != null ? formatCurrency(r.amount) : '—'}
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3 text-center">
                                            <RefundStatusBadge status={r.refundStatus} />
                                            {r.refundStatus === 'FAILED' && r.failReason && (
                                                <p className="text-xs text-gray-400 mt-1 max-w-[180px] mx-auto truncate"
                                                   title={r.failReason}>
                                                    {r.failReason}
                                                </p>
                                            )}
                                            {r.refundStatus === 'PROCESSED' && r.processedAt && (
                                                <p className="text-xs text-gray-400 mt-1 whitespace-nowrap">
                                                    {formatDateTime(r.processedAt)}
                                                </p>
                                            )}
                                        </td>

                                        {/* Contagem de tentativas */}
                                        <td className="px-4 py-3 text-center text-xs text-gray-500">
                                            {r.retryCount > 0
                                                ? <span className="font-medium">{r.retryCount}/{5}</span>
                                                : '—'}
                                        </td>

                                        {/* Ação de retry */}
                                        <td className="px-4 py-3 text-center">
                                            {r.canRetry && (
                                                <button
                                                    onClick={() => handleRetry(r)}
                                                    disabled={retrying === r.id}
                                                    title="Tentar novamente"
                                                    className="flex items-center gap-1 px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                                                >
                                                    {retrying === r.id
                                                        ? <AiOutlineLoading className="animate-spin" size={13} />
                                                        : <MdRefresh size={13} />}
                                                    Retry
                                                </button>
                                            )}
                                            {r.refundStatus === 'FAILED_PERMANENT' && (
                                                <span className="text-xs text-red-400 italic">Suporte</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {loading && <LoadingSpinner />}

            {hasMore && !loading && (
                <div className="text-center mt-4">
                    <button
                        onClick={() => load(false, offset)}
                        className="text-sm text-custom_yellow hover:underline font-medium">
                        Carregar mais
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function CarteiraDigital() {
    const { hasPermission, user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const clientCode = sessionStorage.getItem('clientCode');

    return (
        <div className="h-full flex flex-col">
            <TopNavBar
                title="Carteira Digital"
                subtitle="Saldo, extrato, antecipações e transferências da sua conta digital."
            />
            <div className="mx-4 sm:mx-10 my-6 flex-1 font-poppins">
                {/* Tabs */}
                <div className="flex gap-1 mb-6 border-b border-gray-200">
                    {TABS.map((tab, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveTab(i)}
                            className={`px-5 py-2.5 text-sm font-medium transition-all rounded-t-lg -mb-px
                                ${activeTab === i
                                    ? 'border border-b-white border-gray-200 bg-white text-custom_yellow'
                                    : 'text-gray-500 hover:text-gray-700'}`}>
                            {tab}
                        </button>
                    ))}
                </div>

                {activeTab === 0 && <ResumoTab clientCode={clientCode} />}
                {activeTab === 1 && <ExtratoTab clientCode={clientCode} />}
                {activeTab === 2 && <ReembolsosTab clientCode={clientCode} />}
                {activeTab === 3 && <TaxasTab clientCode={clientCode} />}

                {user?.asaasEnabled && (
                    <div className="flex justify-end mt-6">
                        <AsaasSeal size="md" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default CarteiraDigital;
